import '@/styles/globals.scss';
import '@/utils/polyfills';
import type { AppProps } from 'next/app';
import { Router } from 'next/router';
import { useState } from 'react';
import MessageBanner from '@/components/base/MessageBanner';
import { fontClasses } from '@/components/global/Fonts';
import { GlobalStateProvider } from '@/components/global/GlobalState';
import Head, { PageMetaDefaults } from '@/components/global/Head';
import SiteFooter from '@/components/global/SiteFooter';
import ThemeStylesAppLevel from '@/components/global/ThemeStyles/ThemeStylesAppLevel';
import TrackingIncludes from '@/components/global/TrackingIncludes';
import { AuthProps } from '@/components/page/authProps';
import LoginForm from '@/components/private/LoginForm';
import PageInfo from '@/components/private/PageInfo';
import PerformanceCheck from '@/components/private/PerformanceCheck';
import ReloadPage from '@/components/private/ReloadPage';
import ColorDeficiencyFilters, { queryStringKey as FilterQueryStringKey } from '@/components/private/VisionDeficiencyEmulator';
import { PageHistoryProvider } from '@/contexts/PageHistoryContext';
import { StationLocalizationProvider } from '@/contexts/StationLocalizationContext';
import progressBar from '@/utils/progress-bar';
import styles from './_app.module.scss';

progressBar.handleRouterEvents();

function PageBackgroundLayers() {
  return <>
    <div className={styles.pageBackgroundLayer}>
      <div className={styles.solidColor} />
      <div className={styles.image} />
      <div className={styles.pattern} />
      <div className={styles.gradientOverlay} />
    </div>
  </>;
}

function getQueryStringValue(router: Router, qsp: string, existenceCheckOnly = false): string {
  let qspValue = router.query[qsp];

  if (existenceCheckOnly && typeof qspValue !== 'undefined') {
    return 'true';
  }

  if (qspValue && Array.isArray(qspValue)) {
    qspValue = qspValue[0];
  }

  return qspValue || '';
}

export default function App({ Component, pageProps, router }: AppProps) {
  const [ hasMessageBanner, setHasMessageBanner ] = useState(false);

  // Capture some known QSP values.
  const infoRequested = getQueryStringValue(router, 'info', true);
  const loginRequested = getQueryStringValue(router, 'login', true);
  const filterRequested = getQueryStringValue(router, FilterQueryStringKey);
  const showDevGuides = getQueryStringValue(router, 'showDevGuides');
  const showThemeDevUIRequested = getQueryStringValue(router, 'showThemeDevUI');
  const isCmsPreview = getQueryStringValue(router, 'x-craft-live-preview', true);
  const authProps: AuthProps | undefined = pageProps.authProps;
  let appRequiresLogin = false;

  const internalFeatureFlagStates = [
    filterRequested,
    infoRequested,
    loginRequested,
    showDevGuides === 'true',
    showThemeDevUIRequested === 'true',
  ];

  if (authProps) {
    // If `pageProps.authProps` is not defined, then page doesn't support authentication.

    if (internalFeatureFlagStates.find((value) => value)) {
      // If a requested feature requires a login, then check if a current user is logged in.
      appRequiresLogin = !authProps.user;
    }

    if (appRequiresLogin || pageProps.requireLogin || authProps.shouldRefresh) {
      // Render the login form if a new login is required or if we need to refresh a previous login.
      return <LoginForm authProps={authProps} />;
    }
  }

  if (pageProps.errorPage) {
    if (loginRequested) {
      // Allowing logging in from a 404 page if requested. Useful when QA'ing games without prod builds.
      return <LoginForm authProps={authProps } />;
    }

    // Check if draft preview page
    const version = getQueryStringValue(router, 'version');

    return <div
      className={`${styles.rootWrapper} ${fontClasses.join(' ')}`}
    >
      <Head
        pageDescription={ PageMetaDefaults.DEFAULT_PAGE_DESCRIPTION }
        pageImageUrl={ PageMetaDefaults.DEFAULT_PAGE_IMAGE }
      />

      {version && <ReloadPage versionName={version}/>}

      <div className={styles.mainLayout}>
        <Component {...pageProps} />
        <PageBackgroundLayers />
      </div>
    </div>;
  }

  // ======= RENDER PAGE AS NORMAL =======
  const footerStickerImage = pageProps.pageData?.footerStickerImage || pageProps.contextData?.footerStickerImage;

  return <>
    <div className={fontClasses.join(' ')}>
      <MessageBanner
        messageData={pageProps.siteAlertData}
        onBannerLoaded={ () => setHasMessageBanner(true) } />
      <div
        className={`${styles.rootWrapper}`}
        data-root-wrapper
        data-has-masthead-content={pageProps.hasMastheadContent ? 'true' : 'false'}
        data-has-masthead-theme={pageProps?.resolvedPageThemes?.hasMastheadTheme ? 'true' : 'false'}
        data-dev-guides-on={showDevGuides}
      >
        <GlobalStateProvider>
          <Head
            pageDescription={ PageMetaDefaults.DEFAULT_PAGE_DESCRIPTION }
            pageImageUrl={ PageMetaDefaults.DEFAULT_PAGE_IMAGE }
          />
          <TrackingIncludes />
          <PerformanceCheck />

          {/* Only show page info if logged in */}
          { authProps?.isLoggedin && <PageInfo
            pageData={pageProps?.pageData || pageProps?.collectionData || pageProps?.videoData || pageProps?.gameData}
            drafts={pageProps?.drafts}
            builds={pageProps?.builds}
            isCmsPreview={!!isCmsPreview}
          /> }

          { (authProps?.isLoggedin || filterRequested) && <ColorDeficiencyFilters filter={filterRequested} /> }

          <ThemeStylesAppLevel />

          <PageHistoryProvider>
            <StationLocalizationProvider>
              <div className={styles.mainLayout}>
                <Component {...pageProps } pageHasMessageBanner={ hasMessageBanner } />

                {!pageProps.hideFooter && <SiteFooter
                  hasProducerHeaderFooter={pageProps.hasProducerHeaderFooter}
                  propertyId={pageProps.pageProperty?.id}
                  resolvedPageThemes={pageProps.resolvedPageThemes}
                  footerSticker={footerStickerImage?.[0]?.url}
                />}

                <PageBackgroundLayers />
              </div>
            </StationLocalizationProvider>
          </PageHistoryProvider>
        </GlobalStateProvider>
      </div>
    </div>
  </>;
}
